import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AlertTriangle, AlertCircle, CheckCircle2, ChevronRight, Activity } from "lucide-react";

const VERDICT = {
  healthy:  { color: "text-emerald-300", border: "border-emerald-500/40", icon: CheckCircle2, label: "Healthy" },
  warning:  { color: "text-amber-300",   border: "border-amber-500/40",   icon: AlertCircle,   label: "Warning" },
  critical: { color: "text-red-300",     border: "border-red-500/40",     icon: AlertTriangle, label: "Critical" },
  unknown:  { color: "text-[#A0A0A0]",   border: "border-[#2A2A2A]",      icon: Activity,      label: "No data" },
};

/**
 * UKHealthWidget — drops onto the Admin Console + Admin Shell top.
 *
 * Pings /api/admin/uk-health which combines our provider_usage burn-rate
 * with the owner-entered Emergent balance + auto-recharge config, then
 * surfaces the worst warning level so the founder spots a "trigger=0
 * is too late for video" issue immediately.
 */
export default function UKHealthWidget({ compact = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/uk-health");
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (err) {
    return (
      <div className="border border-red-500/40 bg-red-500/5 p-3 text-xs text-red-300" data-testid="uk-health-error">
        UK health unavailable: {err}
      </div>
    );
  }
  if (!data) return null;

  const v = VERDICT[data.verdict] || VERDICT.unknown;
  const Icon = v.icon;
  const top = data.warnings?.[0];

  if (compact) {
    // Slim banner — for admin shells / dashboard rail
    return (
      <Link
        to="/admin/uk-health"
        data-testid="uk-health-banner"
        className={`flex items-center justify-between gap-3 px-3 py-2 border ${v.border} bg-[#0A0A0A] gold-glow group transition-colors`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={`w-3.5 h-3.5 ${v.color} shrink-0`} />
          <div className="min-w-0">
            <p className={`text-[10px] uppercase tracking-wider font-bold ${v.color}`}>
              UK · {v.label}
            </p>
            <p className="text-[11px] text-[#A0A0A0] truncate">
              {data.balance_credits !== null ? `${data.balance_credits} cr` : "—"}
              {data.runway_days !== null && data.runway_days !== undefined && ` · ${data.runway_days}d runway`}
            </p>
          </div>
        </div>
        <ChevronRight className="w-3.5 h-3.5 text-[#A0A0A0] group-hover:text-[#D4AF37] shrink-0" />
      </Link>
    );
  }

  return (
    <div className={`border ${v.border} bg-gradient-to-br from-[#0A0A0A] to-[#121212] p-5 sm:p-6`} data-testid="uk-health-widget">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-2">Universal Key Health</p>
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${v.color}`} />
            <h3 className={`font-display text-2xl font-extrabold ${v.color}`}>{v.label}</h3>
          </div>
        </div>
        <Link to="/admin/uk-health" data-testid="uk-health-manage" className="text-[10px] uppercase tracking-wider text-[#D4AF37] hover:text-[#F0C84B]">
          Manage
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <Stat label="Balance" value={data.balance_credits !== null ? `${data.balance_credits} cr` : "—"} />
        <Stat label="Runway" value={data.runway_days !== null && data.runway_days !== undefined ? `${data.runway_days} d` : "—"} />
        <Stat label="Video calls · 7d" value={`${data.burn.last_7d.ok}`} sub={`${data.burn.last_7d.failed} failed`} />
        <Stat label="Daily avg" value={`${data.burn.last_7d.daily_avg_calls}`} sub="calls / day" />
      </div>

      {data.warnings?.length > 0 && (
        <div className="space-y-2" data-testid="uk-health-warnings">
          {data.warnings.map((w, i) => {
            const styles = {
              critical: "border-red-500/40 bg-red-500/5 text-red-200",
              warning:  "border-amber-500/40 bg-amber-500/5 text-amber-200",
              info:     "border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#D4AF37]",
            }[w.level];
            const WIcon = w.level === "critical" ? AlertTriangle : AlertCircle;
            return (
              <div key={i} data-testid={`uk-warn-${w.code}`} className={`flex items-start gap-2 p-3 border ${styles} text-xs leading-relaxed`}>
                <WIcon className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>{w.message}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">{label}</p>
      <p className="font-display text-xl font-extrabold text-[#F4F4F0]">{value}</p>
      {sub && <p className="text-[10px] text-[#5A5A5A] mt-0.5">{sub}</p>}
    </div>
  );
}
